// libs
import React, { memo, useEffect, useMemo, useState, useCallback } from 'react';

// global components
import Loader from 'shared/components/Loader';
import LoginBottomSheetWrapper from 'shared/components/LoginBottomSheetWrapper';

// context
import CustomerContext from './context';

// hooks
import { useCustomerInfo, useIsUserDataFetching, useIsUserLoggedIn } from 'shared/store/app/hooks';

// constants
import { AUTH_STATUS, NUPI_CONFIG } from 'shared/constants/auth';

// helpers
import { isUserInitiated, setUserInitiated } from './helpers';
import { transformCustomer } from './transformer';

// analytics
import { setUser } from './analytics';

// hooks
import { useRemoteConfig } from 'shared/store/remoteConfigs/hooks';

// defs
import { Customer } from 'shared/types/customer';

interface CustomerProviderProps {
  children: React.ReactNode;
}

function CustomerProvider({ children }: CustomerProviderProps) {
  const [authStatus, setAuthStatus] = useState(AUTH_STATUS.NOT_DETERMINED);
  const [customer, setCustomer] = useState<Customer | undefined>();
  const [showLoginPopup, setShowLoginPopup] = useState(false);
  const [isLoginForced, setIsLoginForced] = useState(false);
  const [redirectURLonLogin, setRedirectURL] = useState('');
  const [showTruecaller, setShowTruecaller] = useState(true);

  const { isEnabledOnCart: isNupiEnabled } = useRemoteConfig(NUPI_CONFIG);

  const isUserFetching = useIsUserDataFetching();
  const isLoggedIn = useIsUserLoggedIn();
  const customerInfo = useCustomerInfo();

  const onLoginRequested = useCallback(
    (
      redirectURL?: string,
      loginOptions?: {
        disableTruecaller: boolean;
        forcedLogin?: boolean;
      }
    ) => {
      setShowLoginPopup(true);
      setShowTruecaller(!loginOptions?.disableTruecaller);
      loginOptions?.forcedLogin && setIsLoginForced(true);
      redirectURL && setRedirectURL(redirectURL);
    },
    []
  );

  const onLoginRejected = () => {
    setShowLoginPopup(false);
  };

  useEffect(() => {
    if (isUserFetching) {
      return;
    }

    setUserInitiated();

    if (isLoggedIn === false) {
      setUser('GUEST');
      setAuthStatus(AUTH_STATUS.GUEST);
      return;
    }

    const transformedCustInfo = transformCustomer(customerInfo);

    setCustomer(transformedCustInfo);
    setUser(transformedCustInfo.customerData);
    setAuthStatus(AUTH_STATUS.LOGGED_IN);
  }, [customerInfo, isLoggedIn, isUserFetching]);

  const values = useMemo(
    () => ({
      authStatus,
      customer,
      initiateLogin: onLoginRequested,
    }),
    [authStatus, customer, onLoginRequested]
  );

  // this check is done only once for users who might not have PHPSESSID set
  if (isUserInitiated() === false && authStatus === AUTH_STATUS.NOT_DETERMINED) {
    return <Loader />;
  }

  return (
    <CustomerContext.Provider value={values}>
      {children}
      {showLoginPopup && (
        <LoginBottomSheetWrapper
          hide={onLoginRejected}
          isUserDataFetching={isUserFetching}
          isUserLoggedIn={isLoggedIn}
          redirectUrl={redirectURLonLogin}
          showTruecaller={showTruecaller}
          isLoginForced={isLoginForced}
          isNupiEnabled={isNupiEnabled}
        />
      )}
    </CustomerContext.Provider>
  );
}

export default memo(CustomerProvider);
